import type { ThemeConfig } from '@routeroyal/theme-core';

import { AllysAgentsSection } from './components/AgentsSection';
import { AllysBlogArticle } from './components/AllysBlogArticle';
import { AllysBlogCard } from './components/AllysBlogCard';
import { AllysBlogFilters } from './components/AllysBlogFilters';
import { AllysBlogGrid } from './components/AllysBlogGrid';
import { AllysBlogHero } from './components/AllysBlogHero';
import { AllysBlogSearch } from './components/AllysBlogSearch';
import { AllysPageContent } from './components/AllysPageContent';
import { AllysPageHeader } from './components/AllysPageHeader';
import { AllysPageTableOfContents } from './components/AllysPageTableOfContents';
import { AllysPostArticle } from './components/AllysPostArticle';
import { AllysPostCard } from './components/AllysPostCard';
import { AllysPostFilters } from './components/AllysPostFilters';
import { AllysPostGrid } from './components/AllysPostGrid';
import { AllysPostHero } from './components/AllysPostHero';
import { AllysPostPagination } from './components/AllysPostPagination';
import { AllysPostSearch } from './components/AllysPostSearch';
import { AllysContactForm } from './components/ContactForm';
import { AllysCTASection } from './components/CTASection';
import { AllysFeaturedPropertiesCarousel } from './components/FeaturedPropertiesCarousel';
import { AllysFooter } from './components/Footer';
import { AllysHero } from './components/Hero';
import { AllysLocaleSwitcher } from './components/LocaleSwitcher';
import { AllysNavigation } from './components/Navigation';
import { AllysPropertyCard } from './components/PropertyCard';
import { AllysServiceCard } from './components/ServiceCard';
import { AllysServicesSection } from './components/ServicesSection';
import { AllysStatsSection } from './components/StatsSection';
// NOTE: AllysMapComponent is NOT imported here to avoid SSR issues with Leaflet
// It's exported as a named export below but not included in theme config

export const allysTheme: ThemeConfig = {
  id: 'allys',
  name: 'Allys Properties',
  slug: 'allys',
  category: 'Real Estate',
  description: 'Ultra-modern real estate theme inspired by Airbnb, Zillow, and Realtor.com',
  previewImage: '/themes/allys/preview.jpg',
  
  defaultColors: {
    primary: '#243462',       // Deep navy blue - professional and sophisticated
    secondary: '#040813',     // Very dark blue/black - elegant dark elements
    accent: '#BBA339',        // Gold/brass - luxury accent
    accentLight: '#E4CE4A',   // Light gold - secondary accent
    tertiary: '#121C3B',      // Dark blue - alternative dark shade
    background: '#FFFFFF',    // Pure white - clean background
    surface: '#F8F9FA',       // Off-white - subtle surfaces
    text: '#040813',          // Very dark blue/black - primary text
    textMuted: '#6B7280',     // Neutral grey - secondary text
    border: '#E5E7EB',        // Light grey - subtle divisions
  },

  defaultFonts: {
    heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  },
  
  components: {
    Hero: AllysHero,
    PropertyCard: AllysPropertyCard,
    ServiceCard: AllysServiceCard,
    ServicesSection: AllysServicesSection,
    FeaturedPropertiesCarousel: AllysFeaturedPropertiesCarousel,
    AgentsSection: AllysAgentsSection,
    Navigation: AllysNavigation,
    Footer: AllysFooter,
    ContactForm: AllysContactForm,
    LocaleSwitcher: AllysLocaleSwitcher,
    StatsSection: AllysStatsSection,
    CTASection: AllysCTASection,
    // Blog Components (Legacy - Static)
    BlogCard: AllysBlogCard,
    BlogHero: AllysBlogHero,
    BlogSearch: AllysBlogSearch,
    BlogFilters: AllysBlogFilters,
    BlogArticle: AllysBlogArticle,
    BlogGrid: AllysBlogGrid,
    // Post Components (Dynamic - Route Royal Documents)
    PostCard: AllysPostCard,
    PostGrid: AllysPostGrid,
    PostHero: AllysPostHero,
    PostSearch: AllysPostSearch,
    PostFilters: AllysPostFilters,
    PostArticle: AllysPostArticle,
    PostPagination: AllysPostPagination,
    // Page Components (Dynamic - Route Royal Static Pages)
    PageContent: AllysPageContent,
    PageHeader: AllysPageHeader,
    PageTableOfContents: AllysPageTableOfContents,
    // NOTE: MapComponent is excluded from theme config to avoid SSR issues
    // Use ProjectLocationMap wrapper component instead for server components
  },
  
  layoutConfig: {
    homepage: [
      { component: 'Hero', order: 1 },
      { 
        component: 'ServicesSection', 
        order: 2,
      },
      { 
        component: 'FeaturedPropertiesCarousel',
        order: 3,
        props: {
          viewAllHref: '/properties'
        }
      },
      {
        component: 'AgentsSection',
        order: 4,
        props: {
          columns: 2
        }
      },
      { 
        component: 'CTASection', 
        order: 5,
      },
      // Footer is rendered separately at the end of the page
    ],
  },
};

export * from './components/AgentsSection';
export * from './components/CurrencySwitcher';
export * from './components/Footer';
export * from './components/GrainTexture';
export * from './components/Hero';
export * from './components/LocaleSwitcher';
export * from './components/Navigation';
export * from './components/PropertyCard';
export * from './components/PropertyFeatureSection';
export * from './components/PropertyGrid';
export * from './components/ServiceCard';

// Explicitly export new components
export { AllysAdvancedFiltersModal } from './components/AllysAdvancedFiltersModal';
export { AllysBasicFilters } from './components/AllysBasicFilters';
export { AllysFilterPanel } from './components/AllysFilterPanel';
export { AllysPagination } from './components/AllysPagination';
export { AllysPropertyListCard } from './components/AllysPropertyListCard';
export { AllysPropertyMapCard } from './components/AllysPropertyMapCard';
export { AllysPropertyMapView } from './components/AllysPropertyMapView';
export { AllysSearchBar } from './components/AllysSearchBar';
export { AllysSortControl } from './components/AllysSortControl';
export { AllysViewSwitcher } from './components/AllysViewSwitcher';
export { AllysCTASection } from './components/CTASection';
export { AllysFeaturedPropertiesCarousel } from './components/FeaturedPropertiesCarousel';
export { AllysPropertyFinder } from './components/PropertyFinder';
export { AllysPropertyListing } from './components/PropertyListing';
export { AllysPropertyListView } from './components/PropertyListView';
export { AllysPropertySearchBar } from './components/PropertySearchBar';
export { AllysServicesSection } from './components/ServicesSection';
export { AllysStatsSection } from './components/StatsSection';

// About Page Components
export { AllysCompanyStory } from './components/AllysCompanyStory';
export { AllysLocationsGrid } from './components/AllysLocationsGrid';
export { AllysPageHero } from './components/AllysPageHero';
export { AllysTeamSection } from './components/AllysTeamSection';
export { AllysValuesSection } from './components/AllysValuesSection';

// Contact Page Components
export { AllysContactFormSection } from './components/AllysContactFormSection';
export { AllysContactInformationSection } from './components/AllysContactInformationSection';
export { AllysContactMapHero } from './components/AllysContactMapHero';
export { AllysContactSection } from './components/AllysContactSection';
export { AllysCurrencyInput } from './components/AllysCurrencyInput';
export { AllysPhoneInput } from './components/AllysPhoneInput';

// Property Detail Page Components
export { AllysPropertyAmenities } from './components/AllysPropertyAmenities';
export { AllysPropertyContactCard } from './components/AllysPropertyContactCard';
export { AllysPropertyFeatures } from './components/AllysPropertyFeatures';
export { AllysPropertyGallery } from './components/AllysPropertyGallery';
export { AllysPropertyHero } from './components/AllysPropertyHero';
export { AllysPropertyLocation } from './components/AllysPropertyLocation';
export { AllysPropertyOverview } from './components/AllysPropertyOverview';

// Blog/News Page Components (Legacy - Static)
export { AllysBlogArticle } from './components/AllysBlogArticle';
export { AllysBlogCard } from './components/AllysBlogCard';
export { AllysBlogFilters } from './components/AllysBlogFilters';
export { AllysBlogGrid } from './components/AllysBlogGrid';
export { AllysBlogHero } from './components/AllysBlogHero';
export { AllysBlogSearch } from './components/AllysBlogSearch';

// Post Components (Dynamic - Route Royal Documents)
export { AllysPostArticle } from './components/AllysPostArticle';
export { AllysPostCard } from './components/AllysPostCard';
export { AllysPostFilters } from './components/AllysPostFilters';
export { AllysPostGrid } from './components/AllysPostGrid';
export { AllysPostHero } from './components/AllysPostHero';
export { AllysPostPagination } from './components/AllysPostPagination';
export { AllysPostSearch } from './components/AllysPostSearch';

// Page Components (Dynamic - Route Royal Static Pages)
export { AllysPageContent } from './components/AllysPageContent';
export { AllysPageHeader } from './components/AllysPageHeader';
export { AllysPageTableOfContents } from './components/AllysPageTableOfContents';

// Projects Page Components
export { AllysProjectCard, type AllysProjectCardProps } from './components/AllysProjectCard';
export { AllysProjectDetailContent } from './components/AllysProjectDetailContent';
export { AllysProjectDetailHero } from './components/AllysProjectDetailHero';
export { AllysProjectHeroCarousel, type AllysProjectHeroCarouselProps } from './components/AllysProjectHeroCarousel';
export { AllysProjectShowcase, type AllysProjectShowcaseProps } from './components/AllysProjectShowcase';
export { AllysProjectsListView, type AllysProjectsListViewProps } from './components/AllysProjectsListView';

// Map Component
// NOTE: AllysMapComponent is NOT exported from barrel to avoid SSR issues with Leaflet
// Import directly: import { AllysMapComponent } from '@routeroyal/theme-allys/src/components/AllysMapComponent'
// Or better, use ProjectLocationMap wrapper from @/components/projects for server component compatibility
export type { AllysMapComponentProps } from './components/AllysMapComponent';

// Coming Soon Page Component
export { AllysComingSoon, type AllysComingSoonProps } from './components/AllysComingSoon';

// Royale Spaces Components
export { AllysImagineSpaceButton } from './components/AllysImagineSpaceButton';
export { AllysSpacesCTA } from './components/AllysSpacesCTA';
export { AllysSpacesHero } from './components/AllysSpacesHero';
export { AllysSpacesModal } from './components/AllysSpacesModal';
export { AllysSpacesShowcase } from './components/AllysSpacesShowcase';
export { AllysSpacesWizard } from './components/AllysSpacesWizard';

// Area/Neighbourhood Page Components
export { AllysAreaHero, type AllysAreaHeroProps } from './components/AllysAreaHero';
export { AllysAreaMap, type AllysAreaMapProps } from './components/AllysAreaMap';
export { AllysAreaOverview, type AllysAreaOverviewProps } from './components/AllysAreaOverview';
export { AllysAreaPage, type AllysAreaPageProps } from './components/AllysAreaPage';
export { AllysAreaProperties, type AllysAreaPropertiesProps } from './components/AllysAreaProperties';
export { AllysAreaSelector, type AllysAreaSelectorProps } from './components/AllysAreaSelector';
export { AllysAreaStats, type AllysAreaStatsProps } from './components/AllysAreaStats';

// Listing Components (with agent avatars and Allys styling)
export { AllysAgentContactPopover, type ListingAgent as AllysListingAgent } from './components/AllysAgentContactPopover';
export { AllysListingCard, type AllysListingCardProps } from './components/AllysListingCard';
export { AllysListingGrid, AllysListingGridSkeleton, type AllysListingGridProps } from './components/AllysListingGrid';
export { AllysListingMapCard, type AllysListingMapCardProps } from './components/AllysListingMapCard';
export { AllysListingsContent } from './components/AllysListingsContent';
export { AllysListingsMapView, type AllysListingsMapViewProps } from './components/AllysListingsMapView';

// Page Skeleton Loaders (for Next.js loading.tsx instant transitions)
export {
  AllysAboutSkeleton,
  AllysAreaSkeleton,
  AllysArticleSkeleton,
  AllysContactSkeleton,
  AllysHomeSkeleton,
  AllysNavSkeleton,
  AllysNewsSkeleton,
  AllysProjectDetailSkeleton,
  AllysProjectsSkeleton,
  AllysPropertiesSkeleton,
  AllysPropertyDetailSkeleton,
} from './components/skeletons';

